import { createContext, useState, useEffect, useContext } from 'react';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { AgentChatRoomQuery } from 'interfaceAdapter/queries/AgentChatRoom';
import { AgentChatRepository } from 'interfaceAdapter/repositories/AgentChat';
import { useLoading } from 'application/contexts/useLoading';
import { IChatRoom } from 'domain/entities/ChatRoom/ChatRoom';
import { IChat } from 'domain/entities/Chat/Chat';
import { useParams } from 'react-router-dom';

type AgentChatContext = {
    loading: boolean;
    showSnackbar: boolean;
    shwAgreementDialog: boolean;
    unreadIds: number[];
    setShwAgreementDialog: (value: boolean) => void;
    setShowSnackbar: (value: boolean) => void;
    chatRooms: IChatRoom[];
    filteredChatRooms: IChatRoom[];
    selectedChatRoom: IChatRoom | null;
    setSelectedChatRoom: (chatRoom: IChatRoom) => void;
    message: string;
    setMessage: (message: string) => void;
    keyword: string;
    setKeyword: (keyword: string) => void;
    sendMessage: () => void;
    chatMessages: IChat[];
    setMeetingDate: (meetingDate: string, closeModal: () => void) => void;
    confirmMeetingDate: () => void;
    declineMeetingDate: () => void;
    fetchData: () => void;
};

const defaultContext: AgentChatContext = {
    loading: false,
    showSnackbar: false,
    shwAgreementDialog: false,
    unreadIds: [],
    setShwAgreementDialog: () => {},
    setShowSnackbar: () => {},
    chatRooms: [],
    filteredChatRooms: [],
    selectedChatRoom: null,
    setSelectedChatRoom: () => {},
    message: '',
    setMessage: () => {},
    keyword: '',
    setKeyword: () => {},
    sendMessage: () => {},
    chatMessages: [],
    setMeetingDate: () => {},
    confirmMeetingDate: () => {},
    declineMeetingDate: () => {},
    fetchData: () => {},
};

export const agentChatContext = createContext<AgentChatContext>(defaultContext);

export const useAgentChat = (): AgentChatContext => {
    const diContainer = useContext(diContainerContext);
    const agentChatRoomQuery = diContainer.resolve(AgentChatRoomQuery);
    const agentChatRepository = diContainer.resolve(AgentChatRepository);
    const { loading, setLoading } = useLoading();
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [shwAgreementDialog, setShwAgreementDialog] = useState(false);
    const [chatRooms, setChatRooms] = useState<IChatRoom[]>([]);
    const [unreadIds, setUnreadIds] = useState<number[]>([]);
    const [filteredChatRooms, setFilteredChatRooms] = useState<IChatRoom[]>([]);
    const [selectedChatRoom, setSelectedChatRoom] = useState<IChatRoom | null>(null);
    const [keyword, setKeyword] = useState('');
    const [message, setMessage] = useState('');
    const [chatMessages, setChatMessages] = useState<IChat[]>([]);
    const params = useParams();

    const fetchData = async () => {
        const data = await agentChatRoomQuery.fetchRoomList();
        setChatRooms(data);
        const unreadIds = data.filter((chatRoom) => chatRoom.unread).map((chatRoom) => chatRoom.id);
        setUnreadIds(unreadIds);
        setLoading(false);
    };

    useEffect(() => {
        if (keyword.trim()) {
            const filtered = chatRooms.filter((chatRoom) =>
                chatRoom.match.company.name.toLowerCase().includes(keyword.toLowerCase()),
            );
            setFilteredChatRooms(filtered);
        } else {
            setFilteredChatRooms(chatRooms);
        }
    }, [keyword, chatRooms]);

    // チャットメッセージ送信
    const sendMessage = async () => {
        if (!selectedChatRoom) return;
        setLoading(true);
        try {
            await agentChatRepository.send(selectedChatRoom.id, message);
            setMessage('');
            const data = await agentChatRoomQuery.fetchMessages(selectedChatRoom.id);
            setChatMessages(data);
        } finally {
            setLoading(false);
        }
    };

    // 面談日を設定する
    const setMeetingDate = async (meetingDate: string, closeModal: () => void) => {
        if (!selectedChatRoom) return;
        setLoading(true);
        try {
            await agentChatRepository.setMeetingDate(selectedChatRoom.match.id, meetingDate);
            const data = await agentChatRoomQuery.fetchRoomList();
            setChatRooms(data);
            closeModal();
            setShowSnackbar(true);
        } finally {
            setLoading(false);
        }
    };

    // 面談日リクエストを承諾する
    const confirmMeetingDate = async () => {
        if (!selectedChatRoom) return;
        setLoading(true);
        try {
            await agentChatRepository.confirmMeetingDate(selectedChatRoom.match.id);
            const data = await agentChatRoomQuery.fetchRoomList();
            setChatRooms(data);
            setSelectedChatRoom((prev) => {
                const updatedChatRoom = data.find((room) => room.id === prev?.id);
                return updatedChatRoom ? updatedChatRoom : prev;
            });
            setShowSnackbar(true);
        } finally {
            setLoading(false);
        }
    };

    // 面談日リクエストを拒否する
    const declineMeetingDate = async () => {
        if (!selectedChatRoom) return;
        setLoading(true);
        try {
            await agentChatRepository.declineMeetingDate(selectedChatRoom.match.id);
            const data = await agentChatRoomQuery.fetchRoomList();
            setChatRooms(data);
            setSelectedChatRoom((prev) => {
                const updatedChatRoom = data.find((room) => room.id === prev?.id);
                return updatedChatRoom ? updatedChatRoom : prev;
            });
            setShowSnackbar(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (params.chat_room_id === undefined) return;
        const chatRoomId = Number(params.chat_room_id);
        if (!chatRoomId) return;
        const selected = chatRooms.find((chatRoom) => chatRoom.id === chatRoomId);
        if (selected) {
            setSelectedChatRoom(selected);
        }
    }, [params, chatRooms]);

    useEffect(() => {
        if (!selectedChatRoom) return;
        const fetchMessages = async () => {
            const data = await agentChatRoomQuery.fetchMessages(selectedChatRoom.id);
            setChatMessages(data);
            // 既読にする
            setUnreadIds((prev) => prev.filter((id) => id !== selectedChatRoom.id));
        };
        fetchMessages();
    }, [selectedChatRoom, params]);

    return {
        loading,
        showSnackbar,
        shwAgreementDialog,
        unreadIds,
        setShwAgreementDialog,
        setShowSnackbar,
        chatRooms,
        filteredChatRooms,
        selectedChatRoom,
        setSelectedChatRoom,
        message,
        setMessage,
        keyword,
        setKeyword,
        sendMessage,
        chatMessages,
        setMeetingDate,
        confirmMeetingDate,
        declineMeetingDate,
        fetchData,
    };
};
